import { useApiClient } from '@/composables/useApiClient'

import type { IAuthResponse } from './interfaces/auth-response.interface'
import type { IForgotPassword } from './interfaces/forgot-password.interface'
import type { ILogin } from './interfaces/login.interface'
import type { IResetPassword } from './interfaces/reset-password.interface'
import type {
  IVerifyResetToken,
  IVerifyResetTokenResponse,
} from './interfaces/verify-reset-token.interface'

// Relativa en desarrollo por lo mismo que `API_URL`: el proxy de Vite le quita
// el `/voice`, igual que el middleware `api-strip` de Traefik.
const PASSWORD_RECOVERY_API_URL = import.meta.env['VITE_VOICE_API_URL'] ||
  (import.meta.env.PROD
    ? `${window.location.origin}/voice/api`
    : '/voice/api')

export const useAuthService = () => {
  const publicApi = useApiClient(false)
  const privateApi = useApiClient(true)

  const login = async (credentials: ILogin) => {
    return publicApi.login<IAuthResponse>('/auth/login', credentials)
  }

  const me = async () => {
    return privateApi.get<unknown>('/auth/me')
  }

  const logout = async () => {
    return privateApi.post<void>('/auth/logout')
  }

  const getGoogleAuthUrl = async () => {
    return publicApi.get<{ url: string }>('/auth/google')
  }

  const handleGoogleCallback = async (code: string) => {
    return publicApi.get<IAuthResponse>(`/auth/google/callback?code=${code}`)
  }

  const forgotPassword = async (data: IForgotPassword) => {
    return publicApi.post<{ message: string }>(
      `${PASSWORD_RECOVERY_API_URL}/auth/forgot-password`,
      data,
    )
  }

  const verifyResetToken = async (data: IVerifyResetToken) => {
    return publicApi.post<IVerifyResetTokenResponse>(
      `${PASSWORD_RECOVERY_API_URL}/auth/verify-reset-token`,
      data,
    )
  }

  const resetPassword = async (data: IResetPassword) => {
    return publicApi.post<{ message: string }>(
      `${PASSWORD_RECOVERY_API_URL}/auth/reset-password`,
      data,
    )
  }

  return {
    login,
    me,
    logout,
    getGoogleAuthUrl,
    handleGoogleCallback,
    forgotPassword,
    verifyResetToken,
    resetPassword,
  }
}
